import { useMemo, useState } from 'react'

import type { BookAiSection } from '@/api/services/book-ai'
import { Button } from '@/components/ui/button'
import { useCurrentPurpose } from '@/store/useBookAiPurposeStore'

import { collectKeySentences } from './book-ai-position'
import { PassageList } from './purpose-tab'
import { KeyPointList } from './skim-level-ui'

// 最初に出す重要文の数（核心・重要を先に出す）
const INITIAL_POINTS = 5
// 章の中で目的に効く箇所は、要約の下に出すので最初は少なくする
const CHAPTER_PASSAGES = 2

/** 章の中で目的に効く箇所（読む目的があるときだけ） */
export const ChapterPurposePassages = ({
  episodeId,
  sectionIndex,
  onNavigate,
}: {
  episodeId: number
  sectionIndex: number
  onNavigate: (key: number) => void
}) => {
  const passages = useCurrentPurpose(episodeId)?.map?.passages
  const inSection = useMemo(
    () => (passages ?? []).filter((p) => p.section_index === sectionIndex).sort((a, b) => a.key - b.key),
    [passages, sectionIndex],
  )
  if (inSection.length === 0) return null
  return (
    <PassageList
      passages={inSection}
      onNavigate={onNavigate}
      title="この章で目的に効く箇所"
      initialCount={CHAPTER_PASSAGES}
    />
  )
}

/** 章の重要箇所（AI が選んだ重要文。重要度の高いものから出し、押すとその箇所へ移動する） */
export const ChapterKeyPoints = ({
  section,
  onNavigate,
}: {
  section: BookAiSection
  onNavigate: (key: number) => void
}) => {
  const [expanded, setExpanded] = useState(false)
  const points = useMemo(() => {
    const all = collectKeySentences([section], 3)
    // 重要度の高い順に選び、表示は本文の順にする
    const picked = expanded ? all : [...all].sort((a, b) => a.level - b.level).slice(0, INITIAL_POINTS)
    return picked.sort((a, b) => a.key - b.key)
  }, [section, expanded])
  const total = section.analysis?.key_sentences.length ?? 0
  if (total === 0) return null

  return (
    <section className="space-y-1.5">
      <h3 className="text-xs font-semibold text-muted-foreground">この章の重要箇所</h3>
      <KeyPointList points={points} onNavigate={onNavigate} />
      {total > INITIAL_POINTS && (
        <Button variant="ghost" size="sm" className="w-full text-xs" onClick={() => setExpanded(!expanded)}>
          {expanded ? '重要なものだけにする' : `すべて表示（${total}）`}
        </Button>
      )}
    </section>
  )
}
